'use client';

import { useState } from 'react';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketStatus,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, UIOrderType } from '@velocity-exchange/common';
import Button from 'src/components/primitives/Button/Button';
import TableActionButton from 'src/components/Buttons/TableActionButton';
import useModal from 'src/components/Modals/hooks/useModal';
import useClosePositionWithMarketOrder from 'src/hooks/positions/useClosePositionWithMarketOrder';
import useSettlePnls from 'src/hooks/velocity-actions/useSettlePnls';
import { AccountOpenPosition } from 'src/@types/positions';

interface ActionsCellProps {
	position: AccountOpenPosition;
}

export function ActionsCell({ position }: ActionsCellProps) {
	const { marketIndex, baseSize, marketAccount, isIsolatedBalanceOnly } =
		position;

	const [settlingMarketIndex, setSettlingMarketIndex] = useState<
		number | undefined
	>();

	const { show } = useModal();
	const { settlePnls } = useSettlePnls();

	const isMarketInSettlementMode = ENUM_UTILS.match(
		marketAccount.status,
		MarketStatus.SETTLEMENT
	);

	const { closePositionWithMarketOrder, isPending: isClosingPosition } =
		useClosePositionWithMarketOrder({
			marketIndex,
			baseSize: BigNum.from(baseSize.abs(), BASE_PRECISION_EXP),
		});

	const handleClosePosition = (
		e: React.MouseEvent<HTMLButtonElement>,
		uiOrderType: UIOrderType
	) => {
		e.stopPropagation();

		if (uiOrderType === 'market') {
			closePositionWithMarketOrder();
			return;
		}

		show({
			type: 'close_position',
			props: { marketIndex, uiOrderType },
		});
	};

	if (isMarketInSettlementMode || isIsolatedBalanceOnly) {
		return (
			<TableActionButton
				className="justify-start px-0"
				label="Settle"
				handleClick={async () => {
					setSettlingMarketIndex(marketIndex);
					try {
						await settlePnls(
							`position-row-settle-position-${marketIndex}-${Date.now()}`,
							marketIndex
						);
					} finally {
						setSettlingMarketIndex(undefined);
					}
				}}
				isLoading={settlingMarketIndex === marketIndex}
			/>
		);
	}

	return (
		<div className="flex w-full items-center justify-center">
			<div className="flex items-center justify-end gap-sm">
				<Button
					variant="secondary"
					size="sm"
					onClick={(e) => handleClosePosition(e, 'market')}
					disabled={isClosingPosition}
					dataTestId="position-close-market"
				>
					Market
				</Button>
				<Button
					variant="secondary"
					size="sm"
					onClick={(e) => handleClosePosition(e, 'limit')}
					disabled={isClosingPosition}
					dataTestId="position-close-limit"
				>
					Limit
				</Button>
			</div>
		</div>
	);
}
